/** Rendering arbitrary JSON from a preflight action's `detail()` as readable text.
 *
 *  Every read-state panel had its own one-liner for this, and each one gave up
 *  at the first object: `String(v)` printed `[object Object]`, a bare
 *  `JSON.stringify` printed an un-truncated, uncopyable wall. Both are values
 *  the operator was supposed to be reading.
 */

/** Values that read fine on one line, as-is. */
export function isScalar(v: unknown): boolean {
  return v == null || ['string', 'number', 'boolean'].includes(typeof v);
}

/** A scalar in the words the panels use: `—` for absent, `true`/`false` for bools. */
export function formatScalar(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

/** How many items of a list to name before summarising the rest. */
const LIST_PREVIEW = 4;

/**
 * Any JSON value as one readable line.
 *
 * Lists of scalars are joined; lists of objects are counted (they belong in a
 * table, not a line); an object names its keys. Nothing here ever returns
 * `[object Object]`, and nothing returns more than a line's worth — a caller
 * wanting the whole value shows {@link formatJson} instead.
 */
export function formatValue(v: unknown): string {
  if (isScalar(v)) return formatScalar(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    if (v.every(isScalar)) {
      const shown = v.slice(0, LIST_PREVIEW).map(formatScalar);
      return v.length > LIST_PREVIEW
        ? `${shown.join(', ')} +${v.length - LIST_PREVIEW} more`
        : shown.join(', ');
    }
    return `${v.length} item${v.length === 1 ? '' : 's'}`;
  }
  const keys = Object.keys(v as Record<string, unknown>);
  if (keys.length === 0) return '—';
  const shown = keys.slice(0, LIST_PREVIEW).join(', ');
  return keys.length > LIST_PREVIEW ? `${shown} +${keys.length - LIST_PREVIEW} more` : shown;
}

/** The full value, pretty-printed — for a disclosure the operator opens on purpose. */
export function formatJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2) ?? String(v);
  } catch {
    return String(v);
  }
}

/** `branch_io_domain` → `Branch io domain`. A key is better than nothing when no
 *  label was supplied, and every one of these is a snake_case config path. */
export function humanizeKey(key: string): string {
  const words = key.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
